import { firestore } from "@/firebase";
import {
  arrayRemove,
  arrayUnion,
  doc,
  documentId,
  getDoc,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { atom } from "jotai";
import {
  DocumentResultAtom,
  observeFirestoreDocumentAtom,
} from "./atoms/observeFirestoreDocumentAtom";
import {
  BelongsToRelationPropsOfType,
  CombinedQueryConstraint,
  Entity,
  EntityQueryResults,
  EntityRecord,
  Model,
  PartialEntityRecord,
} from "./types";

export type CreateEntityOptions<T extends EntityRecord> = {
  model: Model<T>;
  id: string;
  data?: T | null;
};

export const createEntity = <T extends EntityRecord>(
  options: CreateEntityOptions<T>,
): Entity<T> => {
  let { data } = options;
  const { model, id } = options;
  const docRef = doc(firestore, model.collectionPath, id);

  const hydrate = async () => {
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      result.data = snapshot.data() as T;
    } else {
      throw new Error("Document does not exist");
    }

    return snapshot.data() as T;
  };

  const getObserveEntityRecordAtom: () => DocumentResultAtom<T> = () => {
    return observeFirestoreDocumentAtom<T>(docRef);
  };

  const update = async (newData: PartialEntityRecord<T>) => {
    const result = await setDoc(
      docRef,
      { ...newData, updatedAt: Timestamp.now() },
      { merge: true },
    );

    data = { ...data, ...newData } as T;

    return result;
  };

  const remove = async () => {
    return update({
      status: "trashed",
    } as Partial<T>);
  };

  const createBelongsToRelation = async <K extends EntityRecord>(
    entity: Entity<K>,
    propId: BelongsToRelationPropsOfType<T, K>,
  ) => {
    return setDoc(
      docRef,
      {
        [propId]: arrayUnion(entity.id),
      },
      { merge: true },
    );
  };

  const removeBelongsToRelation = async <K extends EntityRecord>(
    entity: Entity<K>,
    propId: BelongsToRelationPropsOfType<T, K>,
  ) => {
    return setDoc(
      docRef,
      {
        [propId]: arrayRemove(entity.id),
      },
      { merge: true },
    );
  };

  const getManyBelongingsConstraints = <J extends EntityRecord>(
    propId: BelongsToRelationPropsOfType<J, T>,
  ) => {
    return [
      [
        where(propId as string, "array-contains", id),
        `${propId as string} ,array-contains,${id}`,
      ] as CombinedQueryConstraint,
    ];
  };

  const getObserveManyBelongingsAtom = <J extends EntityRecord>(
    model: Model<J>,
    propId: BelongsToRelationPropsOfType<J, T>,
    options?: { constraints?: CombinedQueryConstraint[] },
  ) => {
    const constraints = [
      ...getManyBelongingsConstraints(propId),
      ...(options?.constraints || []),
    ];

    return model.getObserveEntitiesAtom({
      constraints,
    });
  };

  const getObserveManyOwnersAtom = <J extends EntityRecord>(
    model: Model<J>,
    propId: BelongsToRelationPropsOfType<T, J>,
    options?: { constraints?: CombinedQueryConstraint[] },
  ) => {
    return atom((getAtomValue) => {
      const d = getAtomValue(getObserveEntityRecordAtom());

      const ids = (d?.data?.[propId] || []) as string[];

      if (!ids.length) {
        return getAtomValue(
          atom<EntityQueryResults<J>>({
            entities: [],
            loading: false,
            error: undefined,
            snapshot: undefined,
          }),
        );
      }

      const constraints = [
        [
          where(documentId(), "in", ids),
          `id,in,${ids?.join(",")}`,
        ] as CombinedQueryConstraint,
        ...(options?.constraints || []),
      ];

      return getAtomValue(
        model.getObserveEntitiesAtom({
          constraints,
        }),
      );
    });
  };

  const result = {
    model,
    id,
    data,
    docRef,
    hydrate,
    update,
    remove,
    createBelongsToRelation,
    removeBelongsToRelation,
    getObserveEntityRecordAtom,
    getObserveManyBelongingsAtom,
    getObserveManyOwnersAtom,
  };

  return result;
};
